import { ClickAwayListener, SlotComponentProps } from "@mui/base";
import {
  alpha,
  InputBase,
  InputBaseProps,
  Paper,
  Popper,
  setRef,
  TextField,
  TextFieldProps,
  Theme,
} from "@mui/material";
import { SxProps } from "@mui/system";
import { TimeField, TimeFieldProps } from "@mui/x-date-pickers";
import { TimeFieldOwnerState } from "@mui/x-date-pickers/TimeField/TimeField.types";
import { isDatesEquals } from "@sinch/utils/dateTime/dateFns";

import { DateTime } from "luxon";
import { omit } from "ramda";
import React, { FocusEventHandler, forwardRef, PointerEventHandler, useCallback, useRef, useState } from "react";
import { useTimeInput } from "./hooks";
import { AutocompletePaper, AutocompletePopper } from "./TimeInputSlots";
import { TimeList } from "./TimeList/TimeList";
import { TimeInputBaseProps } from "./types";

/**
 * Base time input without label, helper text etc...
 */
export const TimeInputBase = forwardRef<HTMLDivElement, TimeInputBaseProps>(
  (
    {
      AutocompleteProps: autocompleteProps = {},
      error,
      minTime,
      maxTime,
      minutesStep = 15,
      onChange,
      timePoint,
      value,
      dateInOption = true,
      disabled,
      optionsRangeInMinutes,
      inputRef: inputRefProp,
      disablePortal = true,
      referenceDate,
      timeItemsStyle,
      limitInclude,
      ...props
    },
    ref
  ) => {
    // Plain `debounce()` from MUI is recreated every render, so a pending call from one render can be
    // orphaned by a re-render before it fires. Track the pending value ourselves so blur can flush it
    // synchronously instead of racing it (see `flushDebouncedOnChange`).
    const pendingValueRef = useRef<DateTime | null>(null);
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const debouncedOnChange = useCallback(
      (currentVal: DateTime | null) => {
        pendingValueRef.current = currentVal;
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          debounceTimeoutRef.current = null;
          onChange(currentVal);
        }, 200);
      },
      [onChange]
    );

    // Blur (and the section-deselect bookkeeping MUI runs right after it) can read `value` before the
    // 200 ms debounce above commits a freshly typed value. Flush it immediately on blur so any blur-adjacent
    // code always sees the value the user just typed instead of the stale pre-edit one.
    const flushDebouncedOnChange = useCallback(() => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
        onChange(pendingValueRef.current);
      }
    }, [onChange]);

    const { options, onChangeHandler, onBlur } = useTimeInput({
      value,
      minutesStep,
      minTime,
      maxTime,
      autocompleteProps,
      onChange: debouncedOnChange,
      optionsRangeInMinutes: optionsRangeInMinutes ?? 60 * 24 * 7,
      referenceDate,
      limitInclude,
    });

    // Change to max, min value after change focus of section. `newSelectedSections` is null when the field
    // loses focus (blur); skip the clamp then, since `value` is still the pre-edit prop until the 200 ms
    // debounce commits, and clamping against it would stomp a value the user just typed within bounds.
    const handleChangeSectionsChange: TimeFieldProps<any>["onSelectedSectionsChange"] = (newSelectedSections) => {
      if (newSelectedSections === null) {
        return;
      }
      if (minTime && value && value?.valueOf() < minTime?.valueOf()) {
        debouncedOnChange(minTime);
      }
      if (maxTime && value && value?.valueOf() > maxTime?.valueOf()) {
        debouncedOnChange(maxTime);
      }
    };
    const [dropdownAnchorEl, setDropdownAnchorEl] = useState<HTMLInputElement | null>(null);
    const [focused, setFocused] = useState<boolean>(false);
    const popupOpen = Boolean(dropdownAnchorEl);

    const handleFocus: FocusEventHandler<HTMLInputElement> = (event) => {
      setFocused(true);
      setDropdownAnchorEl(event.target);
    };
    const handlePointerDown: PointerEventHandler<HTMLInputElement> = (event) => {
      if (focused) {
        setDropdownAnchorEl(event.target as HTMLInputElement);
      }
    };

    const handleChangeSelected = useCallback(
      (val: DateTime | null) => {
        setDropdownAnchorEl(null);
        onChangeHandler(val);
      },
      [onChangeHandler]
    );

    return (
      <>
        <TimeField<DateTime>
          ref={ref}
          {...omit(["as", "className", "anchorEl"], props)}
          disabled={disabled}
          inputProps={{
            ...props.inputProps,
            "data-cy": "time-input",
            onFocus: handleFocus,
            onBlur: () => {
              setFocused(false);
            },
            onPointerDown: handlePointerDown,
          }}
          inputRef={(inputEl) => {
            setRef<any>(inputRefProp, inputEl);
          }}
          maxTime={maxTime}
          minTime={minTime}
          onBlur={() => {
            flushDebouncedOnChange();
            setDropdownAnchorEl(null);
            onBlur();
          }}
          onChange={onChangeHandler}
          onSelectedSectionsChange={handleChangeSectionsChange}
          readOnly={props.readOnly}
          referenceDate={referenceDate}
          slotProps={{
            textField: {
              error: error,
              onFocus: props.onFocus,
              sx: {
                ...props.sx,
                height: "100%",
                input: {
                  height: "100%",
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
            } as SlotComponentProps<typeof TextField, unknown, TimeFieldOwnerState<DateTime, false>>,
          }}
          slots={{ textField: TimePickerInputBase }}
          value={value}
        />
        <AutocompletePopper
          anchorEl={dropdownAnchorEl}
          as={Popper}
          className="MuiAutocomplete-popper"
          data-cy="time-input-popper"
          disablePortal={disablePortal}
          open={popupOpen && !props.readOnly}
          placement="bottom-start"
          role="presentation"
        >
          <ClickAwayListener
            onClickAway={() => {
              if (!focused) {
                setDropdownAnchorEl(null);
              }
            }}
          >
            <AutocompletePaper
              as={Paper}
              className="MuiPaper-root MuiPaper-elevation MuiPaper-rounded MuiPaper-elevation1 MuiAutocomplete-paper"
            >
              <TimeList
                dateInOption={dateInOption}
                limitInclude={limitInclude}
                maxTime={maxTime}
                minTime={minTime}
                minutesStep={minutesStep}
                minWidth={dropdownAnchorEl ? dropdownAnchorEl.clientWidth : undefined}
                onChange={handleChangeSelected}
                options={options}
                selectedTime={value}
                timeItemsStyle={
                  timeItemsStyle ??
                  ((item): SxProps<Theme> => {
                    if (value && !isDatesEquals(item, value?.toJSDate())) {
                      return (theme) => ({
                        backgroundColor: alpha(theme.palette.warning.main, 0.07),
                      });
                    }
                    return null;
                  })
                }
                timePoint={timePoint}
              />
            </AutocompletePaper>
          </ClickAwayListener>
        </AutocompletePopper>
      </>
    );
  }
);
TimeInputBase.displayName = "TimeInputBase";

const TimePickerInputBase = forwardRef<HTMLInputElement, InputBaseProps & { InputProps: TextFieldProps["InputProps"] }>(
  ({ InputProps, ...props }, ref) => <InputBase {...props} {...InputProps} ref={ref} />
);
TimePickerInputBase.displayName = "TimePickerInputBase";
